import Autoplay from "embla-carousel-autoplay";
import useEmblaCarousel from "embla-carousel-react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

const PHOTO_DELAY = 6000;

// Embla 8 has stop(), but no pause(). Resume with the saved delay, then restore
// the full delay on selection. Embla remains the only owner of the slide timer.
export function useAuctionAutoplay(enabled: boolean, reducedMotion: boolean) {
  const remaining = useRef(PHOTO_DELAY);
  const progressRef = useRef<HTMLDivElement>(null);
  const [cycleDelay, setCycleDelay] = useState(PHOTO_DELAY);
  const autoplay = useMemo(
    () =>
      Autoplay({
        delay: cycleDelay,
        playOnInit: false,
        stopOnFocusIn: false,
        stopOnInteraction: true,
        stopOnMouseEnter: false,
      }),
    [cycleDelay]
  );

  const [viewportRef, embla] = useEmblaCarousel(
    { duration: reducedMotion ? 0 : 25, loop: true },
    [autoplay]
  );

  const draw = useCallback(() => {
    // biome-ignore lint/suspicious/noUnnecessaryConditions: React assigns this ref in the filmstrip component.
    if (progressRef.current) {
      progressRef.current.style.transform = `scaleX(${1 - remaining.current / PHOTO_DELAY})`;
    }
  }, []);

  const freeze = useCallback(() => {
    const left = autoplay.timeUntilNext();
    if (left !== null) {
      remaining.current = Math.max(1, Math.min(PHOTO_DELAY, left));
    }
    autoplay.stop();
    draw();
  }, [autoplay, draw]);

  const reset = useCallback(() => {
    remaining.current = PHOTO_DELAY;
    setCycleDelay(PHOTO_DELAY);
    autoplay.reset();
    draw();
  }, [autoplay, draw]);

  useEffect(() => {
    if (!embla) {
      return;
    }
    if (!enabled) {
      freeze();
    } else if (cycleDelay === remaining.current) {
      autoplay.play();
    } else {
      setCycleDelay(remaining.current);
    }
  }, [embla, enabled, autoplay, cycleDelay, setCycleDelay, remaining, freeze]);

  useEffect(() => {
    if (!embla) {
      return;
    }
    let frame = 0;
    const update = () => {
      const left = autoplay.timeUntilNext();
      if (!enabled || left === null) {
        return;
      }
      remaining.current = Math.max(0, Math.min(PHOTO_DELAY, left));
      draw();
      frame = requestAnimationFrame(update);
    };
    const start = () => {
      cancelAnimationFrame(frame);
      if (enabled) {
        frame = requestAnimationFrame(update);
      }
    };
    const stop = () => cancelAnimationFrame(frame);
    const reinitialize = () => {
      if (enabled) {
        autoplay.play();
      }
    };
    embla
      .on("select", reset)
      .on("reInit", reinitialize)
      .on("autoplay:timerset", start)
      .on("autoplay:timerstopped", stop);
    draw();
    start();
    return () => {
      stop();
      embla
        .off("select", reset)
        .off("reInit", reinitialize)
        .off("autoplay:timerset", start)
        .off("autoplay:timerstopped", stop);
    };
  }, [embla, autoplay, enabled, remaining, reset, draw]);
  return { embla, freeze, progressRef, reset, viewportRef };
}
